import { motion } from "framer-motion";
import {
  Building2, IndianRupee, Megaphone, Wrench, CalendarCheck, BarChart3,
  ArrowRight, CheckCircle2, LogIn, Smartphone,
} from "lucide-react";
import { PublicNav, ERP_URL } from "../components/site/PublicNav";
import { RegisterMenu } from "../components/site/RegisterMenu";
import { ContactSection } from "../components/site/ContactSection";
import { PublicFooter } from "../components/site/PublicFooter";
import { Reveal } from "../components/Reveal";

const MODULES = [
  { icon: Building2, title: "Tenant Management", desc: "Every registered society in one live list — admins, units, plans and status, always complete.", testid: "module-tenants" },
  { icon: IndianRupee, title: "Collections & Billing", desc: "Maintenance billing, payment tracking and defaulter visibility across all your societies.", testid: "module-collections" },
  { icon: Megaphone, title: "Notice Board", desc: "Publish announcements to any society instantly, with urgency flags and read tracking.", testid: "module-notices" },
  { icon: Wrench, title: "Complaints & Requests", desc: "Residents raise maintenance tickets; committees assign, track and close them.", testid: "module-complaints" },
  { icon: CalendarCheck, title: "Amenity Bookings", desc: "Clubhouse, gym and hall slots with approvals — no more double bookings.", testid: "module-amenities" },
  { icon: BarChart3, title: "Reports & Insights", desc: "Collection rates, occupancy and expense trends, exportable in one click.", testid: "module-reports" },
];

const STEPS = [
  ["01", "Register your societies", "Add every property you manage in minutes — name, address, units and plan."],
  ["02", "Invite committee & residents", "Role-based access for admins, committee members and residents."],
  ["03", "Run everything from one desk", "Billing, notices, complaints and reports — all societies, one login."],
];

const PLANS = [
  {
    id: "starter", name: "Starter", price: "₹999", period: "/society/mo",
    blurb: "For single societies getting organised.",
    features: ["Up to 100 units", "Notice board & announcements", "Complaints & requests", "Email support"],
    highlight: false,
  },
  {
    id: "growth", name: "Growth", price: "₹2,499", period: "/society/mo",
    blurb: "For active committees that bill monthly.",
    features: ["Up to 300 units", "Everything in Starter", "Collections & maintenance billing", "Amenity bookings", "Priority support"],
    highlight: true,
  },
  {
    id: "enterprise", name: "Enterprise", price: "Custom", period: "",
    blurb: "For builders & managers running many societies.",
    features: ["Unlimited units & societies", "Everything in Growth", "Reports & data export", "Dedicated account manager"],
    highlight: false,
  },
];

const HeroPreview = () => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
    className="card-soft mx-auto mt-16 max-w-4xl overflow-hidden text-left shadow-xl shadow-slate-200/60"
    data-testid="hero-product-preview"
  >
    <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
      <span className="h-3 w-3 rounded-full bg-red-400" />
      <span className="h-3 w-3 rounded-full bg-amber-400" />
      <span className="h-3 w-3 rounded-full bg-emerald-400" />
      <span className="ml-3 font-mono text-[11px] text-slate-400">erp.synqsmc.com</span>
    </div>
    <div className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm font-bold text-slate-900">Tenant Management</p>
        <span className="rounded-full bg-blue-50 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-blue-600">
          All societies
        </span>
      </div>
      {[
        ["Society Hub 132", "Mumbai", "240 units", "Enterprise"],
        ["Lakeview Enclave", "Bengaluru", "320 units", "Enterprise"],
        ["Riverdale Towers", "Hyderabad", "210 units", "Growth"],
        ["Green Valley Residency", "Pune", "180 units", "Growth"],
      ].map(([name, city, units, plan]) => (
        <div key={name} className="flex items-center justify-between border-t border-slate-100 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Building2 size={15} />
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-800">{name}</p>
              <p className="text-xs text-slate-400">{city}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-slate-500 sm:block">{units}</span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[10px] uppercase text-slate-500">{plan}</span>
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
            </span>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

export default function Landing() {
  return (
    <div data-testid="landing-page" className="bg-white">
      <PublicNav />

      <section data-testid="hero-section" className="relative overflow-hidden pb-24 pt-36 sm:pt-44">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 left-1/2 h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-gradient-to-b from-sky-100 via-blue-50 to-transparent blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(37,99,235,0.07)_1px,transparent_0)] [background-size:32px_32px]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 text-center sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 rounded-full bg-blue-600" />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-700">
              Society Management ERP
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-6xl"
            data-testid="hero-headline"
          >
            Every society you manage.{" "}
            <span className="bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent">
              One platform.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subtext"
          >
            SynqSMC ERP brings tenant management, maintenance collections, notices and resident
            requests into a single, calm dashboard — built for society managers, builders and
            committees who run more than one address.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-stretch justify-center gap-4"
            data-testid="hero-cta-group"
          >
            <a href={ERP_URL} data-testid="hero-cta-login" className="btn-primary !px-8 !py-3.5">
              <LogIn size={16} />
              <span className="text-left leading-tight">
                Login
                <span className="block text-[10px] font-medium text-blue-100">erp.synqsmc.com</span>
              </span>
            </a>
            <RegisterMenu
              testid="hero-cta-register-society"
              variant="hero"
              label="Register"
              sub="User or society"
              align="center"
            />
            <span
              data-testid="hero-download-app"
              aria-disabled="true"
              title="The SynqSMC Android app is coming soon to Google Play"
              className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-8 py-3.5 text-sm font-semibold text-slate-400"
            >
              <Smartphone size={16} />
              <span className="text-left leading-tight">
                Download Android App
                <span className="block font-mono text-[9px] font-semibold uppercase tracking-wider text-amber-500">Coming soon</span>
              </span>
            </span>
          </motion.div>

          <HeroPreview />
        </div>
      </section>

      <section data-testid="stats-strip" className="border-y border-slate-100 bg-slate-50/60 py-12">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-5 text-center sm:grid-cols-3 sm:px-8">
          {[
            ["2,400+", "Societies onboarded"],
            ["₹120 Cr+", "Maintenance collected"],
            ["99.9%", "Platform uptime"],
          ].map(([num, label]) => (
            <Reveal key={label}>
              <p className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{num}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="features" data-testid="features-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 sm:px-8">
        <Reveal className="mx-auto mb-14 max-w-2xl text-center">
          <p className="eyebrow mb-4">Features</p>
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Everything a society office does, unified
          </h2>
          <p className="mt-4 text-slate-600">
            Each module works standalone — and snaps together when you are ready for more.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m, i) => (
            <Reveal key={m.title} delay={0.06 * i}>
              <div
                data-testid={m.testid}
                className="card-soft group h-full p-7 transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100"
              >
                <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-colors duration-300 group-hover:bg-blue-600 group-hover:text-white">
                  <m.icon size={22} />
                </span>
                <h3 className="text-lg font-bold text-slate-900">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="how-it-works" data-testid="how-section" className="border-y border-slate-100 bg-slate-50/60 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <p className="eyebrow mb-4">How it works</p>
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Live in a day, not a quarter
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {STEPS.map(([num, title, desc], i) => (
              <Reveal key={num} delay={0.08 * i}>
                <div className="card-soft h-full p-7" data-testid={`step-${num}`}>
                  <p className="font-mono text-sm font-semibold text-blue-600">{num}</p>
                  <h3 className="mt-3 text-lg font-bold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" data-testid="pricing-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 sm:px-8">
        <Reveal className="mx-auto mb-14 max-w-2xl text-center">
          <p className="eyebrow mb-4">Pricing</p>
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Simple pricing per society
          </h2>
          <p className="mt-4 text-slate-600">
            No setup fees. Cancel anytime. Your data stays yours.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={0.08 * i}>
              <div
                data-testid={`pricing-card-${p.id}`}
                className={`card-soft relative flex h-full flex-col p-8 ${
                  p.highlight ? "border-blue-500 shadow-xl shadow-blue-100 ring-2 ring-blue-100" : ""
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-4 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{p.blurb}</p>
                <p className="mt-5">
                  <span className="text-4xl font-extrabold tracking-tight text-slate-900">{p.price}</span>
                  <span className="text-sm text-slate-400">{p.period}</span>
                </p>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-blue-600" /> {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  data-testid={`pricing-cta-${p.id}`}
                  className={`mt-8 ${p.highlight ? "btn-primary" : "btn-ghost"} w-full`}
                >
                  Request demo
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="about" data-testid="about-section" className="border-y border-slate-100 bg-slate-50/60 py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow mb-4">About Us</p>
              <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                Built for the way Indian societies actually run
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.12}>
              <p className="text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="about-copy">
                SynqSMC started with a simple observation: housing societies run on WhatsApp
                groups, spreadsheets and paper registers — and things fall through the cracks.
                We built SynqSMC ERP so committees and professional managers can run billing,
                notices, complaints and amenities from one dependable platform, and residents
                always know what is happening at their address.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Today the platform serves societies across Mumbai, Pune, Bengaluru, Gurugram and
                Hyderabad — from 50-unit walk-ups to 300-unit townships.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="cta-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 to-sky-500 px-8 py-16 text-center sm:px-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.15)_1px,transparent_0)] [background-size:24px_24px]" />
            <div className="relative">
              <h2 className="mx-auto max-w-2xl text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                Bring every society you run onto one platform
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-blue-100">
                Sign in at erp.synqsmc.com — or request a demo and we will set you up.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <a href={ERP_URL} data-testid="cta-login-button"
                   className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-bold text-blue-700 transition-all duration-300 hover:shadow-xl">
                  Login to the ERP <ArrowRight size={16} />
                </a>
                <a href="#contact" data-testid="cta-demo-button"
                   className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                  Request a demo
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
                {["No setup fees", "Cancel anytime", "Data stays yours"].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-sm text-blue-100">
                    <CheckCircle2 size={15} /> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <ContactSection />
      <PublicFooter />
    </div>
  );
}
